import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Grid, IconButton, Theme, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid";
import {
  IVacationBidCrew,
  IVacationBidCrewListItem,
  IVacationBidCrewUpdateRequest,
  VacationEntitlementAwardChanges,
} from "@vmosolutions/oc-interfaces";

// Components
import {
  EditableDataGrid,
  editableIntegerColumn,
  readOnlyTextColumn,
  useEditableGridPagination,
} from "src/components/EditableDataGrid";
import { TextField } from "src/components/FormikComponents/TextField";
import { CrewStatusFilter, CrewStatusFilterMode } from "./CrewStatusFilter";
import { EntitlementChangeDialog, IEntitlementChange } from "./EntitlementChangeDialog";

import { openSnackbar } from "src/redux/actions/App.Actions";
import { VACATION_BID_TEST_ID } from "../testIds";
import { previewVacationBidCrewEntitlement, updateVacationBidCrew } from "../helpers";
import {
  awardChangesSnackbar,
  createSerialQueue,
  crewName,
  entitlementNotSavedSnackbar,
  entitlementSavedLines,
} from "../Awarding/awardingHelpers";

type CrewRow = IVacationBidCrewListItem;
type EntitlementField = "vacationDays" | "statDays";
type DisplayMode = CrewStatusFilterMode;

/** Non-data column holding the row action button; never highlighted. */
const CREW_ACTIONS_FIELD = "crewActions";

interface IVacationCrewTableProps {
  id: string;
  path: string[];
  packageId: string | undefined;
  /** Package crew plus the preview rows, each carrying its `pendingChange`. */
  rows: CrewRow[];
  crewAdded: string[];
  crewRemoved: string[];
  /** Called with the stored row after an entitlement edit is saved. */
  onRowUpdated: (row: IVacationBidCrew) => void;
  /** Called when a row's remove button is pressed. Preview rows do not offer one. */
  onRemoveCrew: (row: CrewRow) => void;
  /** Shows the grid's loading overlay while the package crew is being fetched. */
  loading?: boolean;
}

const VacationCrewTableStyles = (theme: Theme): any => ({
  root: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    width: "100%",
    overflow: "hidden",
    marginTop: "10px",
    "& .crew_filters": {
      marginLeft: "15px",
      flexWrap: "wrap",
      rowGap: "4px",
      "& .filter_caption": { color: theme.custom.forms.labels, marginRight: "10px" },
      "& .field.textfield": { width: "auto", minWidth: "220px" },
    },
    "& .crew_grid_container": {
      flex: 1,
      minHeight: "200px",
      minWidth: 0,
      width: "100%",
      position: "relative",
      // The grid is pinned out of flow: it contributes nothing to any ancestor's intrinsic width,
      // so its size comes only from this container and can never feed back into it.
      "& > .MuiDataGrid-root": {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
      "& .MuiDataGrid-cell.package_status": {
        fontWeight: 700,
        "&.will_be_added": { color: theme.palette.success.main },
        // Worth noticing, but not an error: crew can sit outside the groups indefinitely.
        "&.outside_groups": { color: theme.palette.warning.main },
      },
    },
    "& .crew_table_footer": {
      color: theme.custom.forms.labels,
      margin: "5px 15px",
    },
  },
});

const PACKAGE_STATUS_LABEL: Record<NonNullable<CrewRow["pendingChange"]>, string> = {
  willBeAdded: "Will be added",
  // States where the row stands rather than predicting its fate: the crew member stays in the
  // package until someone removes them or builds the crew from the groups.
  willBeRemoved: "Not in crew groups",
  unchanged: "",
};

const PACKAGE_STATUS_CLASS: Record<NonNullable<CrewRow["pendingChange"]>, string> = {
  willBeAdded: "will_be_added",
  willBeRemoved: "outside_groups",
  unchanged: "",
};

const getCrewRowId = (row: CrewRow): string => String(row.userId);

/** A committed edit to one entitlement cell, not yet saved. */
interface IEntitlementEdit {
  newRow: CrewRow;
  oldRow: CrewRow;
  field: EntitlementField;
}

/** What the scheduler chose for an edit's award changes. Null cancels the edit. */
type AwardChangesChoice = Pick<IVacationBidCrewUpdateRequest, "awardChanges" | "expected"> | null;

const totalDays = (row: CrewRow): number => (row.vacationDays ?? 0) + (row.statDays ?? 0);

/**
 * A row for a crew member the groups contain but the package does not. It exists only as a preview
 * of what building the crew would add, so it has nothing to edit or remove yet.
 *
 * Crew outside the groups are not previews - they are stored rows, and stay editable and
 * removable like any other.
 */
const isPreviewRow = (row: CrewRow): boolean => row.pendingChange === "willBeAdded";

const matchesTextFilter = (value: string | undefined, filter: string | null): boolean =>
  !filter || (value ?? "").toLowerCase().includes(filter.toLowerCase());

const matchesDisplayMode = (row: CrewRow, mode: DisplayMode): boolean =>
  mode === "all" ||
  (mode === "added" && row.pendingChange === "willBeAdded") ||
  (mode === "removed" && row.pendingChange === "willBeRemoved");

/**
 * Crew in a vacation bid package with their vacation and stat day entitlements. Entitlement cells
 * are saved one at a time as they are committed; a failed save puts the previous value back.
 */
export const VacationCrewTable = (props: IVacationCrewTableProps): JSX.Element => {
  const { id, packageId, rows, crewAdded, crewRemoved, onRowUpdated, onRemoveCrew, loading } = props;
  const dispatch = useDispatch();
  const gridContainerRef = useRef<HTMLDivElement | null>(null);

  const isMounted = useRef<boolean>(false);
  // A question still open when the table goes is settled unanswered, so the edits queued behind it still run.
  const pendingChoiceRef = useRef<((choice: AwardChangesChoice) => void) | null>(null);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      pendingChoiceRef.current?.(null);
    };
  }, []);

  // Working copy of the rows so a committed edit shows at once and can be reverted if the save
  // fails. Re-synced whenever the parent supplies a new row list.
  const [syncedRows, setSyncedRows] = useState<CrewRow[]>(rows);
  const [localRows, setLocalRows] = useState<CrewRow[]>(rows);
  if (rows !== syncedRows) {
    setSyncedRows(rows);
    setLocalRows(rows);
  }

  // Crew filters.
  const [firstNameFilter, setFirstNameFilter] = useState<string | null>(null);
  const [lastNameFilter, setLastNameFilter] = useState<string | null>(null);
  const [employeeIdFilter, setEmployeeIdFilter] = useState<string | null>(null);
  const [displayMode, setDisplayMode] = useState<DisplayMode>("all");

  const filteredRows = useMemo<CrewRow[]>(
    () =>
      localRows.filter(
        (row) =>
          matchesTextFilter(row.firstName, firstNameFilter) &&
          matchesTextFilter(row.lastName, lastNameFilter) &&
          matchesTextFilter(row.employeeId, employeeIdFilter) &&
          matchesDisplayMode(row, displayMode)
      ),
    [localRows, firstNameFilter, lastNameFilter, employeeIdFilter, displayMode]
  );

  const { paginationModel, onPaginationModelChange } = useEditableGridPagination<CrewRow>({
    rows: filteredRows,
    getRowId: getCrewRowId,
    containerRef: gridContainerRef,
  });

  const handleResetFilters = useCallback(() => {
    setFirstNameFilter(null);
    setLastNameFilter(null);
    setEmployeeIdFilter(null);
    setDisplayMode("all");
  }, []);

  // Restores a cell to the value it held before a failed save, but only while it still shows the
  // value that failed. An edit made while the request was in flight is left alone.
  const rollbackRowField = useCallback(
    (userId: string, field: EntitlementField, attempted: number, previous: number) => {
      setLocalRows((rows) =>
        rows.map((row) =>
          getCrewRowId(row) === userId && row[field] === attempted ? { ...row, [field]: previous } : row
        )
      );
    },
    []
  );

  // An edit whose new days call for changes to the crew member's awards, waiting on the scheduler to choose.
  const [awaitingChoice, setAwaitingChoice] = useState<
    (IEntitlementEdit & IEntitlementChange & { decide: (choice: AwardChangesChoice) => void }) | null
  >(null);

  // One edit at a time, from preview to save, so a second cannot replace the first's question or preview stale awards.
  const editQueueRef = useRef<ReturnType<typeof createSerialQueue> | null>(null);
  if (!editQueueRef.current) editQueueRef.current = createSerialQueue();
  const enqueueEdit = editQueueRef.current;

  const rollbackEdit = useCallback(
    ({ newRow, oldRow, field }: IEntitlementEdit) =>
      rollbackRowField(getCrewRowId(newRow), field, newRow[field], oldRow[field]),
    [rollbackRowField]
  );

  const saveEntitlement = useCallback(
    async (edit: IEntitlementEdit, awardChanges: NonNullable<AwardChangesChoice>): Promise<void> => {
      if (!packageId) return;
      const { newRow, field } = edit;
      const response = await updateVacationBidCrew(
        packageId,
        getCrewRowId(newRow),
        { [field]: newRow[field], ...awardChanges },
        { dispatch, errorMessage: "Unable to update entitlement" }
      );
      // A saved row always carries its userId back; an error envelope arrives as a truthy object
      // without one, so the optimistic value has to be put back. Once the table has gone, the
      // snackbar names who was saved, and Add Crew reloads the days when it opens again.
      if (response?.userId) {
        onRowUpdated(response);
        const name = isMounted.current ? undefined : crewName(newRow);
        dispatch(
          openSnackbar(
            awardChangesSnackbar(entitlementSavedLines(response.outcome, name), !!response.outcome?.actions.length)
          )
        );
      } else if (isMounted.current) {
        rollbackEdit(edit);
      }
    },
    [packageId, dispatch, onRowUpdated, rollbackEdit]
  );

  // Checks what the new days do to the crew member's awards first, and asks only when they would change. Days that
  // change nothing are saved even once the table has gone, so a cell committed by clicking away still lands.
  const persistEntitlement = useCallback(
    async (edit: IEntitlementEdit): Promise<void> => {
      if (!packageId) return;
      const { newRow, oldRow, field } = edit;
      const preview = await previewVacationBidCrewEntitlement(
        packageId,
        getCrewRowId(newRow),
        { [field]: newRow[field] },
        { dispatch, errorMessage: "Unable to check what the change does to their awards" }
      );
      if (!preview) {
        if (isMounted.current) rollbackEdit(edit);
        return;
      }
      if (!preview.adjustments.length) {
        await saveEntitlement(edit, {});
        return;
      }
      const choice = isMounted.current
        ? await new Promise<AwardChangesChoice>((decide) => {
            pendingChoiceRef.current = decide;
            setAwaitingChoice({
              ...edit,
              name: crewName(newRow),
              fromDays: totalDays(oldRow),
              toDays: totalDays(newRow),
              preview,
              decide,
            });
          })
        : null;
      pendingChoiceRef.current = null;
      if (choice) await saveEntitlement(edit, choice);
      else if (isMounted.current) rollbackEdit(edit);
      // Their awards would change, and nobody is left to ask, so nothing is saved without the scheduler's say.
      else dispatch(openSnackbar(entitlementNotSavedSnackbar(crewName(newRow))));
    },
    [packageId, dispatch, rollbackEdit, saveEntitlement]
  );

  const choose = useCallback(
    (choice: AwardChangesChoice) => {
      if (!awaitingChoice) return;
      setAwaitingChoice(null);
      awaitingChoice.decide(choice);
    },
    [awaitingChoice]
  );

  // Runs when a cell edit is committed. The edited field is found by comparing the two rows; a
  // preview row or an unchanged value is returned as-is with nothing saved.
  const handleRowCommit = useCallback(
    (newRow: CrewRow, oldRow: CrewRow): CrewRow => {
      const field: EntitlementField | null =
        newRow.vacationDays !== oldRow.vacationDays
          ? "vacationDays"
          : newRow.statDays !== oldRow.statDays
            ? "statDays"
            : null;
      if (field && !isPreviewRow(newRow)) {
        void enqueueEdit(() => persistEntitlement({ newRow, oldRow, field }));
      }
      return newRow;
    },
    [enqueueEdit, persistEntitlement]
  );

  // The grid reports the filtered list back; merge the committed rows into the working copy.
  const handleRowsChange = useCallback((next: CrewRow[]) => {
    const nextById = new Map(next.map((row) => [getCrewRowId(row), row]));
    setLocalRows((previous) => previous.map((row) => nextById.get(getCrewRowId(row)) ?? row));
  }, []);

  const isCellEditable = useCallback((row: CrewRow): boolean => !isPreviewRow(row), []);

  const columns = useMemo<GridColDef<CrewRow>[]>(
    () => [
      readOnlyTextColumn<CrewRow>("firstName", "First Name", { flex: 1 }),
      readOnlyTextColumn<CrewRow>("lastName", "Last Name", { flex: 1 }),
      editableIntegerColumn<CrewRow>("vacationDays", "Vacation Days", { min: 0, flex: 1 }),
      editableIntegerColumn<CrewRow>("statDays", "Stat Days", { min: 0, flex: 1 }),
      readOnlyTextColumn<CrewRow>("employeeId", "Employee Id", { flex: 1 }),
      readOnlyTextColumn<CrewRow>("groupMembership", "Group Membership", {
        flex: 2,
        valueGetter: (params: GridValueGetterParams<CrewRow>) => (params.row.groupMembership ?? []).join(", "),
      }),
      readOnlyTextColumn<CrewRow>("isActive", "Active Status", {
        flex: 1,
        valueGetter: (params: GridValueGetterParams<CrewRow>) => (params.row.isActive ? "ACTIVE" : "INACTIVE"),
      }),
      {
        ...readOnlyTextColumn<CrewRow>("pendingChange", "Package Status", {
          flex: 1,
          valueGetter: (params: GridValueGetterParams<CrewRow>) =>
            params.row.pendingChange ? PACKAGE_STATUS_LABEL[params.row.pendingChange] : "",
        }),
        cellClassName: (params) =>
          `package_status ${params.row.pendingChange ? PACKAGE_STATUS_CLASS[params.row.pendingChange] : ""}`.trim(),
      },
      {
        field: CREW_ACTIONS_FIELD,
        headerName: "",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 52,
        renderCell: (params: GridRenderCellParams<CrewRow>) =>
          // A preview row is not in the package yet, so there is nothing to take out of it.
          isPreviewRow(params.row) ? null : (
            <Tooltip title="Remove from package" placement="left">
              <IconButton
                size="small"
                onClick={() => onRemoveCrew(params.row)}
                data-testid={VACATION_BID_TEST_ID.removeCrewButton}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
      },
    ],
    [onRemoveCrew]
  );

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      className="crew_on_bidpackage_table vacation_crew_table"
      sx={(theme) => VacationCrewTableStyles(theme).root}
    >
      <Grid container alignItems="center" className="crew_filters">
        <Typography variant="caption" color="textPrimary" className="filter_caption">
          Filter Crew
        </Typography>
        <TextField
          className="field textfield surname_filter"
          name="crew_first_name_filter"
          label="First Name"
          value={firstNameFilter}
          onChange={(event) => setFirstNameFilter(event?.target?.value ?? null)}
          type="text"
        />
        <TextField
          className="field textfield givenname_filter"
          name="crew_last_name_filter"
          label="Last Name"
          value={lastNameFilter}
          onChange={(event) => setLastNameFilter(event?.target?.value ?? null)}
          type="text"
        />
        <TextField
          className="field textfield employee_id_filter"
          name="crew_employee_id_filter"
          label="Employee Id"
          value={employeeIdFilter}
          onChange={(event) => setEmployeeIdFilter(event?.target?.value ?? null)}
          type="text"
        />
        <CrewStatusFilter
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          addedCount={crewAdded?.length ?? 0}
          removedCount={crewRemoved?.length ?? 0}
        />
        <Button className="refresh_btn" variant="outlined" onClick={handleResetFilters}>
          Reset All Filters
        </Button>
      </Grid>

      <Box id={`${id}-vacation-crew-grid`} ref={gridContainerRef} className="crew_grid_container">
        <EditableDataGrid<CrewRow>
          data-testid={VACATION_BID_TEST_ID.crewGrid}
          rowTestId={VACATION_BID_TEST_ID.crewRow}
          rows={filteredRows}
          columns={columns}
          getRowId={getCrewRowId}
          onRowsChange={handleRowsChange}
          normalizeRow={handleRowCommit}
          isCellEditable={isCellEditable}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          loading={loading}
          disableRowSelectionOnClick={true}
        />
      </Box>

      <Typography variant="caption" className="crew_table_footer">
        Stat and vacation days are entered manually. Edit to override.
      </Typography>

      <EntitlementChangeDialog
        change={awaitingChoice}
        onApply={() =>
          choose({ awardChanges: VacationEntitlementAwardChanges.Apply, expected: awaitingChoice?.preview.adjustments })
        }
        onDaysOnly={() => choose({ awardChanges: VacationEntitlementAwardChanges.DaysOnly })}
        onCancel={() => choose(null)}
      />
    </Grid>
  );
};
